import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, input, linkedSignal } from '@angular/core'

import { JsonValue } from '@flowx/shared/utils'

type Segment = {
  key: string
  value: JsonValue
  type: undefined | string
  description: string
  expanded: boolean
}

@Component({
  selector: 'flx-json-viewer',
  templateUrl: './json-viewer.component.html',
  styleUrls: ['./json-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule],
})
export class JsonViewerComponent {
  json = input<JsonValue | null>(null)
  expanded = input(true)
  depth = input(-1)

  // what is this used for?
  _currentDepth = input(0)

  segments = linkedSignal<Segment[]>(() => {
    const json = this.json()
    const expanded = this.expanded()
    const depth = this.depth()
    const currentDepth = this._currentDepth()

    if (json == null) {
      return []
    }

    const decycledJson = this.#decycle(json) as JsonValue

    const isExpanded = expanded && !(depth > -1 && currentDepth >= depth)

    if (typeof decycledJson === 'object') {
      return Object.keys(decycledJson).map((key) =>
        this.#parseKeyValue(key, decycledJson[key], isExpanded)
      )
    }

    return [this.#parseKeyValue(`(${typeof decycledJson})`, decycledJson, isExpanded)]
  })

  isExpandable(segment: Segment): boolean {
    return segment.type === 'object' || segment.type === 'array'
  }

  toggle(segment: Segment): void {
    if (!this.isExpandable(segment)) {
      return
    }

    const segments = this.segments()
    const index = segments.indexOf(segment)

    if (index === -1) {
      return
    }

    this.segments.set(segments.map((s, i) => (i === index ? { ...s, expanded: !s.expanded } : s)))
  }

  #parseKeyValue(key: string, value: JsonValue, isExpanded: boolean): Segment {
    const segment: Segment = {
      key,
      value,
      type: undefined,
      description: '',
      expanded: isExpanded,
    }

    switch (typeof value) {
      case 'number': {
        segment.type = 'number'
        segment.description = String(value)
        break
      }
      case 'boolean': {
        segment.type = 'boolean'
        segment.description = String(value)
        break
      }
      case 'function': {
        segment.type = 'function'
        segment.description = 'function'
        break
      }
      case 'string': {
        segment.type = 'string'
        segment.description = `"${value}"`
        break
      }
      case 'undefined': {
        segment.type = 'undefined'
        segment.description = 'undefined'
        break
      }
      case 'object': {
        // yea, null is object
        if (value === null) {
          segment.type = 'null'
          segment.description = 'null'
        } else if (Array.isArray(value)) {
          segment.type = 'array'
          segment.description = `Array[${value.length}] ${JSON.stringify(value)}`
        } else if (value instanceof Date) {
          segment.type = 'date'
          segment.description = Date.prototype.toString.call(value)
        } else {
          segment.type = 'object'
          segment.description = `Object ${JSON.stringify(value)}`
        }
        break
      }
    }

    return segment
  }

  // https://github.com/douglascrockford/JSON-js/blob/master/cycle.js
  #decycle(object: any): any {
    const objects = new WeakMap()
    return (function derez(value, path): any {
      let oldPath
      let nu: any

      if (
        typeof value === 'object' &&
        value !== null &&
        !(value instanceof Boolean) &&
        !(value instanceof Date) &&
        !(value instanceof Number) &&
        !(value instanceof RegExp) &&
        !(value instanceof String)
      ) {
        oldPath = objects.get(value)
        if (oldPath !== undefined) {
          return { $ref: oldPath }
        }
        objects.set(value, path)

        if (Array.isArray(value)) {
          nu = []
          value.forEach((element, i) => {
            nu[i] = derez(element, `${path}[${i}]`)
          })
        } else {
          nu = {}
          Object.keys(value).forEach((name) => {
            nu[name] = derez(value[name], `${path}[${JSON.stringify(name)}]`)
          })
        }
        return nu
      }
      return value
    })(object, '$')
  }
}
